import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection,
  SeverityLevel
} from '@/components/guides/guide-layout';
import { Link } from 'react-router';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function SmallClaimsCourtGuide() {
  return (
    <GuideLayout
      slug="small-claims-court-guide"
      category="Escalation"
      categoryColor="warning"
      title="Small Claims Court: A Complete Guide"
      description="Everything you need to know about escalating your pothole claim to court. It's simpler than you think — and often just filing is enough."
      readTime="10 min"
      updatedDate="12 January 2026"
      reviewedFor="UK court process"
      sources={[
        { name: 'gov.uk', url: 'https://www.gov.uk/make-court-claim-for-money' },
      ]}
      relatedGuides={[
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons', readTime: '6 min' },
        { slug: 'what-to-expect-at-hearing', title: 'What to Expect at Your Hearing', readTime: '8 min' },
        { slug: 'section-58-defence', title: 'The Section 58 Defence', readTime: '7 min' },
      ]}
      atAGlance={[
        { label: 'Claim value limit', value: '£10,000 or less — pothole claims are typically £100-500' },
        { label: 'Court fees', value: '£35-£80 online depending on claim amount, reclaimable if you win' },
        { label: 'Typical timeline', value: '3-6 months from filing to hearing, though many claims settle earlier' },
        { label: 'Legal representation', value: 'Not needed — small claims is designed for people to represent themselves' },
        { label: 'Cost risk if you lose', value: "Generally just your court fee — you don't pay the council's legal costs" },
      ]}
      howTo={{
        name: 'How to Take a Pothole Claim to Small Claims Court',
        description:
          'Five steps to escalate a rejected pothole claim through the County Court small claims track, from filing to judgment.',
        steps: [
          {
            name: 'File your claim',
            text: "Complete form N1 or use Money Claims Online with your details, the council's details, and the particulars of claim — a brief description of what happened and what is being claimed.",
          },
          {
            name: 'Council responds',
            text: 'Once issued, the council has 14-28 days to respond. They can admit the claim, file a defence (usually citing Section 58), make a settlement offer, or ignore it — in which case judgment can be entered in default.',
          },
          {
            name: 'Directions',
            text: 'If defended, the court sends directions — instructions on exchanging evidence and preparing for the hearing. For small claims this usually means sending copies of the evidence to the council and the court by a set date.',
          },
          {
            name: 'The hearing',
            text: 'Small claims hearings are typically 30-60 minutes in a private room. The judge explains the process, hears both sides, asks questions to clarify points, and makes a decision on the day or in writing later.',
          },
          {
            name: 'Judgment',
            text: "The judge decides based on the evidence. A winning claimant is paid the claim plus court fees; a losing claimant gets no compensation but generally doesn't pay the council's costs.",
          },
        ],
      }}
      faqs={[
        {
          question: 'Do I need a solicitor for small claims court?',
          answer: 'No. Small claims court is specifically designed for people to represent themselves without legal help. The process is informal, the judge will guide you through, and the rules on costs mean even if you lose, you won\'t normally pay the council\'s legal fees.',
        },
        {
          question: 'How much does it cost to file a small claim?',
          answer: 'Court fees depend on the claim amount. For claims up to £300, the fee is £35 online (£50 paper). For £300-500, it\'s £50 online (£70 paper). For £500-1,000, it\'s £70 online (£80 paper). If you win, you can claim these fees back.',
        },
        {
          question: 'What happens if I lose my small claims court case?',
          answer: 'In small claims, even if you lose, you generally don\'t have to pay the council\'s legal costs. You\'ll lose the court fee you paid, but that\'s typically the extent of your financial risk. This makes small claims a relatively low-risk option.',
        },
        {
          question: 'How long does the small claims process take?',
          answer: 'From filing to hearing typically takes 3-6 months. The council has 14-28 days to respond, then the court sends directions, and a hearing is scheduled 2-4 months later. Many cases settle before the hearing once the council sees you\'re serious.',
        },
        {
          question: 'Can I file a small claim online?',
          answer: 'Yes, and it\'s cheaper than paper forms. Use Money Claims Online (MCOL) at gov.uk. The online fees are lower, and the process is straightforward. You\'ll need the council\'s legal services address, which is usually available from the council website or rejection letter.',
        },
        {
          question: 'What if the council offers to settle after I file?',
          answer: 'This is very common. Many councils prefer to settle rather than spend staff time on a hearing. You can accept their offer and withdraw the claim, or negotiate if the offer is too low.',
        },
        {
          question: 'What do I need to bring to a small claims hearing?',
          answer: 'Bring copies of all your evidence: photos, repair invoices, location information, any prior reports, the council\'s rejection letter, and your claim documents. Organise them clearly. You\'ll also want notes on the key points you want to make.',
        },
        {
          question: 'Is small claims court formal like on TV?',
          answer: 'No. Small claims hearings are informal. They\'re usually held in a private room (not a courtroom), you sit at a table with the judge, and there are no wigs or gowns. The judge will guide you through the process and ask questions to understand your case.',
        },
        {
          question: 'What are my chances of winning in small claims court?',
          answer: 'No guide can predict the outcome. The court considers the defect-specific evidence, the responsible authority\'s inspection and maintenance records, any verified prior report and response, causation and loss. A third-party report is a lead to verify against the authority\'s own records, not proof of notice or liability.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        }
        title="What is Small Claims Court?"
      >
        <p className="mb-4">
          Small claims court is part of the County Court system, designed for resolving disputes 
          worth £10,000 or less. For{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            pothole claims
          </Link>
          {' '}— typically £100-500 — it's the perfect venue. 
          And here's the key point: <strong>it's designed for ordinary people, not lawyers.</strong>
        </p>
        <GuideTip variant="success" title="Many councils settle when you file">
          Don't think of court as a last resort that you'll definitely have to see through. Many 
          councils prefer to settle rather than spend staff time on a hearing. Just filing your 
          claim is often enough to prompt an offer.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Why Small Claims Works for Pothole Cases"
      >
        <p className="text-brand-200 mb-4">
          Small claims court has features that make it ideal for pothole disputes:
        </p>
        <GuideGrid cols={2}>
          <GuideCard variant="dark" title="No Lawyers Needed">
            The process is designed for litigants in person — you represent yourself.
          </GuideCard>
          <GuideCard variant="dark" title="Limited Cost Risk">
            Even if you lose, you generally don't pay the council's legal costs.
          </GuideCard>
          <GuideCard variant="dark" title="Informal Process">
            No wigs, no gowns — just a straightforward conversation with a judge.
          </GuideCard>
          <GuideCard variant="dark" title="Judge Helps You">
            District judges actively help unrepresented parties understand the process.
          </GuideCard>
        </GuideGrid>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Court Fees"
      >
        <p className="mb-4">
          There's a fee to file your claim. Online filing (Money Claims Online) is cheaper:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-brand-50">
                <th className="border border-surface-200 px-4 py-3 text-left font-semibold text-brand-950">Claim Amount</th>
                <th className="border border-surface-200 px-4 py-3 text-left font-semibold text-brand-950">Online Fee</th>
                <th className="border border-surface-200 px-4 py-3 text-left font-semibold text-brand-950">Paper Fee</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-surface-200 px-4 py-3">Up to £300</td>
                <td className="border border-surface-200 px-4 py-3 font-semibold text-success-600">£35</td>
                <td className="border border-surface-200 px-4 py-3">£50</td>
              </tr>
              <tr className="bg-surface-50">
                <td className="border border-surface-200 px-4 py-3">£300.01 - £500</td>
                <td className="border border-surface-200 px-4 py-3 font-semibold text-success-600">£50</td>
                <td className="border border-surface-200 px-4 py-3">£70</td>
              </tr>
              <tr>
                <td className="border border-surface-200 px-4 py-3">£500.01 - £1,000</td>
                <td className="border border-surface-200 px-4 py-3 font-semibold text-success-600">£70</td>
                <td className="border border-surface-200 px-4 py-3">£80</td>
              </tr>
              <tr className="bg-surface-50">
                <td className="border border-surface-200 px-4 py-3">£1,000.01 - £1,500</td>
                <td className="border border-surface-200 px-4 py-3 font-semibold text-success-600">£80</td>
                <td className="border border-surface-200 px-4 py-3">£115</td>
              </tr>
            </tbody>
          </table>
        </div>
        <GuideTip variant="info">
          If you win, you can claim the court fee back as part of your costs. Filing online 
          through Money Claims Online is both cheaper and faster.
        </GuideTip>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="File Your Claim">
          <p className="mb-2">
            Complete form N1 (or use Money Claims Online) with your details, the council's 
            details, and your "particulars of claim" — a brief description of what happened 
            and what you're claiming.
          </p>
          <GuideTip variant="info">
            Court document preparation is planned for a later Fixtyer release. For now, use the
            official Money Claims Online or N1 guidance when filing.
          </GuideTip>
        </GuideStep>

        <GuideStep number={2} title="Council Responds">
          <p className="mb-2">
            Once issued, the council has 14-28 days to respond. They can:
          </p>
          <GuideGrid cols={2}>
            <GuideCard title="Admit">
              Accept the claim — you win immediately.
            </GuideCard>
            <GuideCard title="Defend">
              File a defence (usually citing{' '}
              <Link to="/guides/section-58-defence" className={inlineLink}>
                Section 58
              </Link>
              ).
            </GuideCard>
            <GuideCard title="Settle">
              Make an offer to resolve out of court.
            </GuideCard>
            <GuideCard title="Ignore">
              You can get judgment in default — automatic win.
            </GuideCard>
          </GuideGrid>
        </GuideStep>

        <GuideStep number={3} title="Directions">
          <p>
            If defended, the court sends "directions" — instructions on exchanging evidence 
            and preparing for the hearing. For small claims, this is usually straightforward: 
            send copies of your evidence to the council and the court by a set date.
          </p>
        </GuideStep>

        <GuideStep number={4} title="The Hearing">
          <p className="mb-2">
            <Link to="/guides/what-to-expect-at-hearing" className={inlineLink}>
              Small claims hearings
            </Link>
            {' '}are typically 30-60 minutes in a private room (not a courtroom). 
            The judge will:
          </p>
          <ul className="list-disc pl-5 space-y-1 text-surface-700">
            <li>Introduce themselves and explain the process</li>
            <li>Ask you to explain your case</li>
            <li>Ask the council to explain their defence</li>
            <li>Ask questions to clarify points</li>
            <li>Make a decision (sometimes on the day, sometimes in writing later)</li>
          </ul>
        </GuideStep>

        <GuideStep number={5} title="Judgment">
          <p>
            The judge decides based on the evidence. If you win, the council pays your claim 
            plus court fees. If you lose, you don't get compensation but generally don't pay 
            their costs either.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Building Your Case"
      >
        <p className="mb-4">For your hearing, you'll need to demonstrate:</p>
        <div className="space-y-3">
          <SeverityLevel level="critical" badge="Essential">
            <strong>The defect existed:</strong> Photos of the pothole, ideally with measurements 
            showing it was 40mm+ deep.
          </SeverityLevel>
          <SeverityLevel level="high" badge="Important">
            <strong>It was dangerous:</strong> Evidence showing this wasn't just minor wear — it 
            posed a real hazard to vehicles.
          </SeverityLevel>
          <SeverityLevel level="high" badge="Important">
            <strong>The road manager:</strong> Verify that this was a publicly maintained road and
            confirm the specific road manager through official records. A postcode result provides
            only a likely-authority lead.
          </SeverityLevel>
          <SeverityLevel level="medium" badge="Strengthens">
            <strong>It caused your damage:</strong> Link between the incident and{' '}
            <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
              your vehicle damage
            </Link>
            {' '}— photos, repair invoice.
          </SeverityLevel>
          <SeverityLevel level="low" badge="Helpful">
            <strong>Prior reports:</strong> A verified, defect-specific report may be relevant to
            notice when considered with its timing, the inspection regime and the authority&apos;s
            response. It does not by itself determine a Section 58 defence.
          </SeverityLevel>
        </div>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Assessing Evidence Relevant to Section 58"
      >
        <p className="text-brand-200 mb-4">
          If the authority relies on Section 58, assess its evidence against the specific defect
          and incident rather than assuming any single fact decides the defence:
        </p>
        <GuideGrid cols={2}>
          <GuideCard variant="dark" title="Prior Reports">
            Verify that any entry concerns the same defect, when and how the authority received it,
            and what response its own records show.
          </GuideCard>
          <GuideCard variant="dark" title="Inspection Gaps">
            Compare the road-specific inspection records with the applicable policy and the
            authority&apos;s evidence about its operation.
          </GuideCard>
          <GuideCard variant="dark" title="Multiple Defects">
            Nearby defects provide context only; they do not establish the condition, notice or
            response for the defect involved in the claim.
          </GuideCard>
          <GuideCard variant="dark" title="Severity">
            Measurements and dated photographs may help the court assess the condition, but must
            be considered with inspection timing, visibility and repair evidence.
          </GuideCard>
        </GuideGrid>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Typical Timeline"
      >
        <GuideGrid cols={2}>
          <GuideCard title="Claim Issued">
            1-2 weeks after you file online
          </GuideCard>
          <GuideCard title="Council Response">
            14-28 days after the claim is served
          </GuideCard>
          <GuideCard title="Directions Sent">
            2-4 weeks after defence filed
          </GuideCard>
          <GuideCard title="Hearing Date">
            Usually 2-4 months after directions
          </GuideCard>
        </GuideGrid>
        <p className="mt-4 text-surface-700">
          <strong>Total time: 3-6 months from filing to hearing.</strong> But remember — settlement 
          can happen at any point. Many claims settle after filing but before the hearing.
        </p>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        title="Should You Go to Court?"
      >
        <p className="mb-4">Consider these factors when deciding:</p>
        <GuideGrid cols={2}>
          <div className="bg-success-500/10 border border-success-500/30 rounded-xl p-5">
            <h4 className="font-semibold text-success-700 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Good Signs
            </h4>
            <ul className="space-y-2 text-sm text-surface-700">
              <li>• The pothole was previously reported</li>
              <li>• You have clear photos showing depth</li>
              <li>• The defect was severe (50mm+)</li>
              <li>• You have a proper repair invoice</li>
              <li>• Council's rejection was vague</li>
            </ul>
          </div>
          <div className="bg-warning-500/10 border border-warning-500/30 rounded-xl p-5">
            <h4 className="font-semibold text-warning-700 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Weaker Position
            </h4>
            <ul className="space-y-2 text-sm text-surface-700">
              <li>• No prior reports found</li>
              <li>• Pothole was shallow (under 40mm)</li>
              <li>• Council has recent inspection records</li>
              <li>• Damage could have other causes</li>
              <li>• Poor quality or missing evidence</li>
            </ul>
          </div>
        </GuideGrid>
        <GuideTip variant="info" title="Even with a weaker case...">
          Filing may still prompt a settlement offer. Councils often prefer to pay a reasonable 
          claim rather than spend staff time on a court case. The act of filing shows you're serious.
        </GuideTip>
      </GuideSection>

      <div className="bg-brand-50 border border-brand-200 rounded-2xl p-6 mb-8">
        <h3 className="font-display text-lg font-bold text-brand-950 mb-4">
          Fixtyer's Planned Court Support:
        </h3>
        <GuideGrid cols={2}>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Guidance for reviewing a council rejection</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Escalation checklist</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Links to official small claims court resources</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Court document preparation planned for a later release</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">Hearing preparation checklist</span>
          </div>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700">What to expect at a small claims hearing</span>
          </div>
        </GuideGrid>
        <p className="text-sm text-surface-600 mt-4">
          Court document preparation is not currently available to purchase. Start with your{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            council rejection
          </Link>
          {' '}and use official court guidance while this feature is developed.
        </p>
      </div>

      <GuideTip variant="success" title="Remember: They're hoping you'll give up">
        Councils reject claims hoping you won't pursue them further. Don't let them win by default. 
        If you have good evidence and a legitimate claim, small claims court is there to help.
      </GuideTip>
    </GuideLayout>
  );
}
